import React, { useMemo } from "react";
import { Alert, Button, Checkbox, Spin, Tag } from "antd";
import { useTranslation } from "react-i18next";
import type { ExternalEndpointOption } from "../../../interfaces/External.interface";
import { groupEndpoints, liveControlIds, type EndpointGroup } from "./apiClientGrants";

interface ApiClientEndpointChecklistProps {
  endpoints: ExternalEndpointOption[];
  /** The checked catalog ids. */
  value: string[];
  onChange: (ids: string[]) => void;
  loading?: boolean;
  disabled?: boolean;
  /**
   * Ids shown checked and fixed: what the client is known to hold when the
   * save cannot revoke (add only), so unchecking one cannot look like a revoke.
   */
  locked?: string[];
  /** Why the catalog cannot be used (failed, or incomplete); shown instead of the list. */
  error?: string;
  testId: string;
}

/** The heading of a group: "Live control", "Reports: faults", or "Other". */
function useGroupTitle(): (group: EndpointGroup) => string {
  const [t] = useTranslation("global");
  return (group) => {
    if (group.key === "live") return t("apiClients.groupLive");
    if (group.key === "other") return t("apiClients.groupOther");
    return t("apiClients.groupReports", { group: group.key.replace(/^reports:/, "") });
  };
}

/**
 * The /api/ext endpoints an API client may call, grouped as the External API
 * guide describes them: the live routes (status, command catalog, commands,
 * feed) and the report routes by family. Each group has its own all/none
 * checkbox, and "Live control" checks every live route in one click.
 */
function ApiClientEndpointChecklist({
  endpoints,
  value,
  onChange,
  loading = false,
  disabled = false,
  error = "",
  locked = [],
  testId,
}: ApiClientEndpointChecklistProps) {
  const [t] = useTranslation("global");
  const groupTitle = useGroupTitle();
  const groups = useMemo(() => groupEndpoints(endpoints), [endpoints]);
  const live = useMemo(() => liveControlIds(endpoints), [endpoints]);
  const fixed = new Set(locked);
  const checked = new Set([...value, ...locked]);

  const setGroup = (group: EndpointGroup, on: boolean) => {
    const ids = new Set(
      group.endpoints.map((endpoint) => endpoint.id).filter((id) => !fixed.has(id)),
    );
    const rest = value.filter((id) => !ids.has(id));
    onChange(on ? [...rest, ...ids] : rest);
  };

  const toggle = (id: string, on: boolean) => {
    onChange(on ? [...value.filter((v) => v !== id), id] : value.filter((v) => v !== id));
  };

  if (loading) return <Spin data-testid={`${testId}-loading`} />;
  if (error) {
    return <Alert type="error" showIcon message={error} data-testid={`${testId}-error`} />;
  }
  if (groups.length === 0) {
    return <Alert type="info" showIcon message={t("apiClients.noEndpoints")} />;
  }

  return (
    <div data-testid={testId}>
      {live.length > 0 && (
        <Button
          size="small"
          data-testid={`${testId}-preset-live`}
          disabled={disabled}
          onClick={() => onChange([...value.filter((id) => !live.includes(id)), ...live])}
        >
          {t("apiClients.presetLive")}
        </Button>
      )}
      {groups.map((group) => {
        const held = group.endpoints.filter((endpoint) => checked.has(endpoint.id)).length;
        const allLocked = group.endpoints.every((endpoint) => fixed.has(endpoint.id));
        return (
          <div key={group.key} className="mt-3" data-testid={`${testId}-group-${group.key}`}>
            <Checkbox
              data-testid={`${testId}-group-toggle-${group.key}`}
              checked={held === group.endpoints.length}
              indeterminate={held > 0 && held < group.endpoints.length}
              disabled={disabled || allLocked}
              onChange={(event) => setGroup(group, event.target.checked)}
            >
              <strong>{groupTitle(group)}</strong>
            </Checkbox>
            <div style={{ marginLeft: 24 }}>
              {group.endpoints.map((endpoint) => (
                <div key={endpoint.id}>
                  <Checkbox
                    data-testid={`${testId}-endpoint-${endpoint.uri}`}
                    checked={checked.has(endpoint.id)}
                    disabled={disabled || fixed.has(endpoint.id)}
                    onChange={(event) => toggle(endpoint.id, event.target.checked)}
                  >
                    {endpoint.verbs.map((verb) => (
                      <Tag key={verb}>{verb}</Tag>
                    ))}
                    <code>{endpoint.uri}</code>
                  </Checkbox>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default ApiClientEndpointChecklist;
